// El metaprompting del Estudio de Video: el MISMO sistema que el modo Redes —
// ADR-2026-10-05-el-estudio-usa-el-metaprompting-de-redes.
//
// Hasta acá el Estudio mandaba el prompt del usuario tal cual: el escritor de video del
// backend sólo corre cuando el body trae `tipo_video` (el Creador), y el de imagen sólo
// con `escribir_prompt` (Redes). Acá viven, puras, las decisiones de qué viaja en cada
// pedido y cuándo un prompt que devolvió "Mejorar prompt" ya está escrito (y no se vuelve
// a escribir, igual que en Redes: `promptYaEscrito`). Sin React ni red, para testearlas.

import { esCambioLeve, type DeclaradoDeImagen } from '../v2/modes/redes/redes';
import type { PromptEscritoDeImagen } from '../../lib/escritorDeImagen';

/** Tipo de video con el que el Estudio enciende el escritor del Creador. */
export const TIPO_DE_VIDEO_DEL_ESTUDIO = 'general';

/** Lo que devolvió "Mejorar prompt" en IMAGEN, con el contexto en que se escribió. */
export interface MejoraDeImagen {
  prompt: string;
  declarado: DeclaradoDeImagen;
  /** Qué es cada foto adjunta, por su mención (`Imagen 1`), dicho por el escritor. */
  etiquetas: Record<string, string>;
  /** `claveDeRefs` de los adjuntos con los que se escribió. */
  refs: string;
  aspecto: string;
}

/** Lo que devolvió "Mejorar prompt" en una ESCENA de video, con su contexto. */
export interface MejoraDeEscena {
  prompt: string;
  duracion: number;
  refs: string;
  aspecto: string;
}

/** Los ids de las referencias como una clave estable: con otra referencia el escritor diría otra cosa. */
export const claveDeRefs = (ids: ReadonlyArray<string | null | undefined>): string =>
  ids.filter((x): x is string => !!x).slice().sort().join(',');

/** Cómo se menciona la i-ésima imagen adjunta (sin `@`): el escritor la nombra así y el
 *  backend reemplaza la mención por el rótulo del motor. */
export const mencionDeAdjunto = (i: number): string => `Imagen ${i + 1}`;

/**
 * Las referencias de IMAGEN como viajan al backend (al escritor suelto y a la generación):
 * cada URL firmada con su mención y, si el escritor ya dijo qué es, su etiqueta.
 */
export function referenciasDeImagen(
  urls: readonly string[],
  etiquetas: Record<string, string> = {},
): Array<{ url: string; tipo: 'adjunto'; mencion: string; etiqueta?: string }> {
  return urls.map((url, i) => {
    const mencion = mencionDeAdjunto(i);
    const etiqueta = etiquetas[mencion];
    return { url, tipo: 'adjunto', mencion, ...(etiqueta ? { etiqueta } : {}) };
  });
}

/** La huella de una mejora de imagen a partir de lo que devolvió el escritor. */
export function mejoraDeImagen(r: PromptEscritoDeImagen, refs: string, aspecto: string): MejoraDeImagen {
  const etiquetas: Record<string, string> = {};
  for (const e of r.etiquetas) etiquetas[e.mencion.replace(/^@/, '').trim()] = e.etiqueta;
  return {
    prompt: r.prompt,
    declarado: {
      tarea: r.tarea,
      edicion: r.edicion,
      pielExpuesta: r.piel_expuesta,
      fondoBlanco: r.fondo_blanco,
      luzFondoBlanco: r.luz_fondo_blanco,
      etiquetas: [],
    },
    etiquetas,
    refs,
    aspecto,
  };
}

/** ¿El prompt que se va a generar ya es el del escritor? Mismo contexto y texto igual o con un retoque leve. */
export function imagenYaEscrita(m: MejoraDeImagen | null, prompt: string, refs: string, aspecto: string): boolean {
  return !!m && !!prompt.trim() && m.refs === refs && m.aspecto === aspecto && esCambioLeve(m.prompt, prompt);
}

export function escenaYaEscrita(
  m: MejoraDeEscena | null,
  prompt: string,
  duracion: number,
  refs: string,
  aspecto: string,
): boolean {
  return (
    !!m &&
    !!prompt.trim() &&
    m.duracion === duracion &&
    m.refs === refs &&
    m.aspecto === aspecto &&
    esCambioLeve(m.prompt, prompt)
  );
}

/**
 * Lo que suma el body de una IMAGEN: si el prompt ya es del escritor, lo que declaró (el
 * backend elige el motor con eso y no vuelve a escribir); si no, `escribir_prompt` y el
 * backend redacta como paso del job. Mismos campos que `generateFreeformImage`.
 */
export function camposDelEscritorDeImagen(
  m: MejoraDeImagen | null,
  prompt: string,
  refs: string,
  aspecto: string,
): Record<string, unknown> {
  if (!imagenYaEscrita(m, prompt, refs, aspecto) || !m) return { escribir_prompt: true };
  const d = m.declarado;
  return {
    tarea: d.tarea,
    ...(d.edicion === 'fusionar' ? { edicion: 'fusionar' } : {}),
    ...(d.pielExpuesta ? { piel_expuesta: true } : {}),
    ...(d.fondoBlanco
      ? {
          background_mode: 'blanco',
          fondo_blanco_temperatura: 'neutro',
          fondo_blanco_iluminacion: d.luzFondoBlanco,
        }
      : {}),
  };
}

/** Lo que suma el body de una ESCENA: `tipo_video` enciende el escritor del Creador (sólo
 *  en escenas generales: las de personas llevan diálogo, no un prompt para redactar), y
 *  `prompt_escrito` lo saltea cuando el texto ya es suyo. */
export function camposDelEscritorDeVideo(sceneMode: string | undefined, promptEscrito: boolean | undefined): Record<string, unknown> {
  if (sceneMode !== 'general') return {};
  return { tipo_video: TIPO_DE_VIDEO_DEL_ESTUDIO, ...(promptEscrito ? { prompt_escrito: true } : {}) };
}
